import React from "react";
import * as S from "./style";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { Skeleton, Typography, Box, Table, TableBody, TableCell, TableHead, TableRow, Paper, Button, Dialog, DialogTitle, DialogContent } from "@mui/material";
import { Grid } from "@mui/system";
import { useLanguage } from "../../contexts/LanguageContext";

interface VitalThreshold {
  vital_type: string;
  flag_type: string;
  threshold_type: string;
  value: number;
}

interface ThresholdsData {
  patient_id: string;
  thresholds: VitalThreshold[];
}

interface PatientProfileProps {
  meta?: any;
  thresholds?: ThresholdsData | null;
  patientId?: string;
  isLoadingThresholds?: boolean;
  /** Accepted for API compatibility; the thresholds dialog is read-only for everyone. */
  readOnly?: boolean;
}

export default function PatientProfile({ meta, thresholds, patientId, isLoadingThresholds = false }: PatientProfileProps) {
  const { t } = useLanguage();
  const profile = meta?.profile;
  const demographics = meta?.demographics;
  const [thresholdsOpen, setThresholdsOpen] = React.useState(false);
  const thresholdRows: VitalThreshold[] = thresholds?.thresholds ?? [];

  const closeThresholdsDialog = () => setThresholdsOpen(false);

  const getVitalLabel = (vitalType: string) => {
    switch (vitalType) {
      case "systolic_blood_pressure": return t('vitals.systolicBP');
      case "diastolic_blood_pressure": return t('vitals.diastolicBP');
      case "heart_rate": return t('vitals.heartRate');
      case "o2_saturation": return t('vitals.spo2');
      default: return vitalType;
    }
  };

  const getFlagLabel = (flagType: string) => {
    return flagType === "critical" ? t('vitals.critical') : t('vitals.warning');
  };

  const getThresholdLabel = (thresholdType: string) => {
    return thresholdType === "high" ? t('vitals.high') : t('vitals.low');
  };

  if (!profile || Object.keys(profile).length === 0) {
    return (
      <S.StyledDivBoxWrapper container spacing={3}>
        <S.StyledTopBar />
        <S.StyledInfoBox>
          <Skeleton variant="text" width={160} height={28} />
          <Skeleton variant="text" width={140} height={18} />
          <Skeleton variant="text" width={200} height={18} />
        </S.StyledInfoBox>

        <S.StyledGridRow container display="flex">
          {Array.from({ length: 4 }).map((_, idx) => (
            <Grid
              key={idx}
              size={{ xs: 3 }}
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={0.5}
            >
              <Skeleton variant="text" width={60} height={18} />
              <Skeleton variant="text" width={50} height={18} />
            </Grid>
          ))}
        </S.StyledGridRow>

        <S.StyledDivider />

        <S.StyledContactContainer>
          <Skeleton variant="text" width={180} height={20} />
          <Skeleton variant="text" width={220} height={20} />
        </S.StyledContactContainer>

        <S.StyledDivider />

        <S.StyledLinkContainer>
          <Skeleton variant="text" width={220} height={20} />
        </S.StyledLinkContainer>
      </S.StyledDivBoxWrapper>
    );
  }

  const weightLabel =
    demographics?.weight_kg !== undefined && Number(demographics.weight_kg) > 0
      ? `${demographics.weight_kg}kg`
      : undefined;
  const heightLabel =
    demographics?.height_m !== undefined && Number(demographics.height_m) > 0
      ? `${demographics.height_m}m`
      : undefined;

  return (
    <S.StyledDivBoxWrapper container spacing={3}>
      <S.StyledTopBar color={profile.color} />
      <S.StyledInfoBox>
        <Typography variant="body1" fontWeight="bold">
          {profile.name}
        </Typography>
        <Typography variant="caption" color="grey">
          {t('patient.id')} #{String(patientId ?? "").slice(0, 8)}
        </Typography>
        <Typography variant="caption" color="grey">
          {profile.address}
        </Typography>
      </S.StyledInfoBox>
      <S.StyledGridRow container display="flex">
        {[
          { label: t('patient.gender'), value: demographics?.gender ?? "-" },
          { label: t('patient.age'), value: demographics?.age ?? "-" },
          { label: t('patient.weight'), value: weightLabel ?? "-" },
          { label: t('patient.height'), value: heightLabel ?? "-" },
        ].map((item, idx) => (
          <Grid
            key={idx}
            size={{ xs: 3 }}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Typography variant="caption">{item.label}</Typography>
            <Typography variant="caption" fontWeight="bold">
              {item.value}
            </Typography>
          </Grid>
        ))}
      </S.StyledGridRow>

      <S.StyledDivider />

      <S.StyledContactContainer>
        <S.StyledContactRow>
          <PhoneIcon fontSize="small" color="action" />
          <Typography variant="caption">{profile.phone}</Typography>
        </S.StyledContactRow>
        <S.StyledContactRow>
          <EmailIcon fontSize="small" color="action" />
          <Typography variant="caption">{profile.email}</Typography>
        </S.StyledContactRow>
      </S.StyledContactContainer>

      {(isLoadingThresholds || thresholds) && (
        <>
          <S.StyledDivider />
          <Box sx={{ width: "100%" }}>
            <Button
              size="small"
              variant="outlined"
              fullWidth
              startIcon={<TuneOutlinedIcon fontSize="small" />}
              disabled={isLoadingThresholds}
              onClick={() => setThresholdsOpen(true)}
              sx={{ textTransform: "none", justifyContent: "flex-start" }}
            >
              {t('vitals.thresholdsTitle')}
            </Button>
          </Box>
        </>
      )}

      <Dialog open={thresholdsOpen} onClose={closeThresholdsDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>{t('vitals.thresholdsTitle')}</Typography>
        </DialogTitle>
        <DialogContent>
          <Paper variant="outlined" sx={{ borderColor: "divider", "& .MuiTableCell-root": { borderColor: "divider" } }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><Typography variant="caption">{t('vitals.thresholdsTable.vital')}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{t('vitals.thresholdsTable.type')}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{t('vitals.thresholdsTable.level')}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption">{t('vitals.thresholdsTable.value')}</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {thresholdRows.map((threshold, index) => (
                  <TableRow key={index}>
                    <TableCell><Typography variant="caption">{getVitalLabel(threshold.vital_type)}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{getThresholdLabel(threshold.threshold_type)}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{getFlagLabel(threshold.flag_type)}</Typography></TableCell>
                    <TableCell align="right">
                      <Typography variant="caption">{threshold.value.toFixed(1)}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </DialogContent>
      </Dialog>
    </S.StyledDivBoxWrapper>
  );
}
